import { twMerge } from "tailwind-merge";

import {
  For,
  type JSX,
  Show,
  batch,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
} from "solid-js";

import AffiliateClaiTip from "@/components/Base/AffiliateClaiTip";
import Icon from "@/components/Icon";
import { type svgPathTypes } from "@/components/Icon";
import { scrollChildSelectorIntoCenterX } from "@/lib/scrollToCenterX";

/** 分段选项类型定义 */
export type SegmentOption = {
  /** 显示的标签文本或JSX元素 */
  label: string | JSX.Element;
  /** 选项的值 */
  value: string | number;
  /** 可选的计数徽章 */
  count?: number;
  /** 可选的图标名称 */
  icon?: svgPathTypes;
  /** 可选的类型 */
  type?: number;
  /** 可选的红点提示 */
  showRedDot?: boolean;
};

/** 方向类型 */
export type SegmentDirection = "horizontal" | "vertical";

/** 分段控制器组件属性 */
export type SegmentControlProps = {
  /** 选项列表 */
  options: SegmentOption[];
  /** 当前选中的值 */
  value?: string | number;
  /** 值变化时的回调函数 */
  onChange?: (value: string | number) => void;
  /** 组件根容器的样式类名 */
  class?: string;
  /** 激活状态的样式类名 */
  activeClass?: string;
  /** 非激活状态的样式类名 hover时使用 */
  inactiveClass?: string;
  /** 容器样式类名 */
  containerClass?: string;
  /** 指示器样式类名 */
  indicatorClass?: string;
  /** 选项项基础样式类名 */
  itemClass?: string;
  /** 是否启用拖动功能，默认为false */
  draggable?: boolean;
  /** 拖动阈值，默认为5px */
  dragThreshold?: number;
  /** 图标激活状态的样式类名 */
  activeIconClass?: string;
  /** 图标非激活状态的样式类名 */
  inactiveIconClass?: string;
  /** 图标尺寸，默认为20px */
  iconSize?: string;
  /** 方向，默认为horizontal */
  direction?: SegmentDirection;
  /** 选项间距，默认为10px */
  itemSpacing?: number;
  /** 目前唯一适用场景 需要自适应宽度时 需要设置listClass */
  listClass?: string;
  /** 图标位置，默认为left */
  iconPosition?: "left" | "right";
  /** 选项内容样式类名 */
  itemContentClass?: string;
  /** 红点提示位置 */
  redDotClass?: string;
};

/** 分段控制器组件 */
export function SegmentControl(props: SegmentControlProps) {
  // 内部激活索引状态
  const [activeIndex, setActiveIndex] = createSignal(0);

  // 指示器样式状态（位置和宽度/高度）
  const [indicatorStyle, setIndicatorStyle] = createSignal({
    width: "0px",
    height: "0px",
    transform: "translateX(0px)",
  });

  // 拖动相关状态
  const [isDragging, setIsDragging] = createSignal(false);
  const [startX, setStartX] = createSignal(0);
  const [startY, setStartY] = createSignal(0);
  const [scrollLeft, setScrollLeft] = createSignal(0);
  const [scrollTop, setScrollTop] = createSignal(0);
  const [containerRef, setContainerRef] = createSignal<HTMLDivElement>();
  const [hasMoved, setHasMoved] = createSignal(false);
  const [isMouseDown, setIsMouseDown] = createSignal(false);

  // 指示器动画控制
  const [enableAnimation, setEnableAnimation] = createSignal(false);

  // 滚动状态控制
  const [isScrolling, setIsScrolling] = createSignal(false);

  // 定时器引用，用于清理
  let hasMovedTimer: ReturnType<typeof setTimeout> | undefined;
  let scrollingTimer: ReturnType<typeof setTimeout> | undefined;

  // 配置常量
  const DRAG_THRESHOLD = props.dragThreshold ?? 5;
  const ICON_SIZE = props.iconSize ?? "20px";
  const DIRECTION = props.direction ?? "horizontal";
  const ITEM_SPACING = props.itemSpacing ?? 10;
  const ICON_POSITION = props.iconPosition ?? "left";
  const DRAGGABLE = props.draggable ?? false;

  /** 计算当前激活的索引 优先使用props.value，否则使用内部activeIndex */
  const currentIndex = createMemo(() => {
    if (props.value !== undefined) {
      return props.options.findIndex((option) => option.value === props.value);
    }
    return activeIndex();
  });

  /** 计算每个选项当前应应用的样式类（激活/非激活/拖动中禁用hover/滚动中禁用hover） */
  const computeItemStateClass = createMemo(() => {
    const current = currentIndex();
    const dragging = DRAGGABLE && isDragging();
    const scrolling = isScrolling();
    const activeClass = props.activeClass ?? "";
    const inactiveClass = props.inactiveClass ?? "";
    console.log(inactiveClass, dragging, scrolling, current, "inactiveClass");
    // 返回一个函数，该函数根据索引计算样式类
    return (optionIndex: number): string => {
      if (current === optionIndex) {
        return activeClass;
      }
      if (dragging || scrolling) {
        return "";
      }
      return inactiveClass;
    };
  });

  /** 判断是否为水平方向 */
  const isHorizontal = createMemo(() => DIRECTION === "horizontal");

  /** 判断是否应该使用 scrollToCenterX */
  const shouldUseScrollToCenterX = createMemo(() => {
    return isHorizontal() && DRAGGABLE;
  });

  /** 获取指定索引的选项元素 */
  const getItemElement = (index: number): HTMLElement | null => {
    const container = containerRef();
    if (!container) return null;

    const items = container.querySelectorAll("[data-segment-item]");
    return (items[index] as HTMLElement) || null;
  };

  // createEffect(() => {
  //   const options = props.options;
  //   if (options.length) {
  //     updateIndicator(currentIndex());
  //   }
  // });

  /**
   * 更新指示器位置和尺寸
   *
   * @param index 目标选项的索引
   */
  const updateIndicator = (index: number, animate: boolean = true) => {
    const container = containerRef();
    if (!container) return;

    // 直接获取指定索引的元素
    const currentItem = getItemElement(index);
    if (!currentItem) return;

    // 直接使用元素的offset位置，更精准
    const offsetPosition = isHorizontal()
      ? currentItem.offsetLeft
      : currentItem.offsetTop;

    // 根据是否需要动画来设置状态
    batch(() => {
      setEnableAnimation(animate);

      // 设置指示器样式
      setIndicatorStyle({
        width: `${currentItem.offsetWidth}px`,
        height: `${currentItem.offsetHeight}px`,
        transform: isHorizontal()
          ? `translateX(${offsetPosition}px)`
          : `translateY(${offsetPosition}px)`,
      });
    });
  };

  /**
   * 处理选项点击事件
   *
   * @param option 被点击的选项
   * @param index 选项索引
   */
  const handleOptionClick = (option: SegmentOption, index: number) => {
    // 如果启用拖动功能且已经移动过，不触发点击事件
    if (DRAGGABLE && hasMoved()) return;

    const container = containerRef();
    if (!container) {
      // 如果没有容器，直接更新状态
      batch(() => {
        setActiveIndex(index);
        props.onChange?.(option.value);
      });
      return;
    }

    // 点击非第一个元素时自动滚动到中心
    if (index > 0) {
      const selector = `[data-segment-item]:nth-child(${index + 1})`;

      // 设置滚动状态
      setIsScrolling(true);
      // 清除之前的滚动定时器
      if (scrollingTimer) {
        clearTimeout(scrollingTimer);
      }

      // 使用 scrollChildSelectorIntoCenterX 滚动到中心
      scrollChildSelectorIntoCenterX(container, selector);

      // 滚动完成后再更新状态和指示器
      setTimeout(() => {
        batch(() => {
          setActiveIndex(index);
          props.onChange?.(option.value);
          updateIndicator(index);
        });

        // 延迟重置滚动状态
        scrollingTimer = setTimeout(() => {
          setIsScrolling(false);
        }, 300);
      }, 200); // 给滚动更多时间完成
    } else {
      // 点击第一个元素时，直接更新状态
      batch(() => {
        setActiveIndex(index);
        props.onChange?.(option.value);
      });
    }
  };

  /** 鼠标按下事件处理 */
  const handleMouseDown = (e: MouseEvent) => {
    // 如果未启用拖动功能，直接返回
    if (!DRAGGABLE) return;

    const container = containerRef();
    if (!container) return;
    // 使用 batch 优化状态更新
    batch(() => {
      setIsMouseDown(true);
      setStartX(e.pageX - container.offsetLeft);
      setStartY(e.pageY - container.offsetTop);
      setScrollLeft(container.scrollLeft);
      setScrollTop(container.scrollTop);
      setHasMoved(false);
    });
  };

  /** 鼠标移动事件处理 */
  const handleMouseMove = (e: MouseEvent) => {
    // 如果未启用拖动功能或鼠标未按下，直接返回
    if (!DRAGGABLE || !isMouseDown()) return;

    const container = containerRef();
    if (!container) return;

    // 如果还没有开始拖动，检查是否需要开始拖动
    if (!isDragging()) {
      const x = e.pageX - container.offsetLeft;
      const y = e.pageY - container.offsetTop;
      const walkX = Math.abs(x - startX());
      const walkY = Math.abs(y - startY());
      const walk = isHorizontal() ? walkX : walkY;

      // 只有当鼠标移动超过阈值时才开始拖动
      if (walk > DRAG_THRESHOLD) {
        batch(() => {
          setIsDragging(true);
          setHasMoved(true);
        });
      } else {
        return;
      }
    }

    // 如果正在拖动，处理滚动
    if (isDragging()) {
      e.preventDefault();

      if (isHorizontal()) {
        const x = e.pageX - container.offsetLeft;
        const walk = x - startX();
        container.scrollLeft = scrollLeft() - walk;
      } else {
        const y = e.pageY - container.offsetTop;
        const walk = y - startY();
        container.scrollTop = scrollTop() - walk;
      }
    }
  };

  /** 鼠标松开事件处理 */
  const handleMouseUp = () => {
    // 如果未启用拖动功能，直接返回
    if (!DRAGGABLE) return;

    batch(() => {
      setIsMouseDown(false);
      setIsDragging(false);
    });

    // 清理之前的定时器
    if (hasMovedTimer) {
      clearTimeout(hasMovedTimer);
    }

    // 延迟重置移动标记，避免立即触发点击事件
    hasMovedTimer = setTimeout(() => {
      setHasMoved(false);
    }, 10);
  };

  /** 监听选项变化，自动更新指示器位置 */
  let timer: ReturnType<typeof setTimeout> | undefined;

  createEffect(
    on(
      () => JSON.stringify([currentIndex(), props.options]),
      () => {
        const index = currentIndex();
        const options = props.options;

        if (index >= 0 && options.length > 0) {
          // 清除之前的防抖定时器
          clearTimeout(timer);

          // 添加防抖
          timer = setTimeout(() => {
            // 如果满足条件，先执行滚动
            if (shouldUseScrollToCenterX()) {
              const container = containerRef();
              if (container) {
                // 清除之前的滚动定时器
                if (scrollingTimer) {
                  clearTimeout(scrollingTimer);
                }

                scrollChildSelectorIntoCenterX(
                  container,
                  `[data-segment-item]:nth-child(${index + 1})`,
                );
                // 滚动完成后再更新指示器
                setTimeout(() => {
                  updateIndicator(index);
                }, 50);

                // 延迟重置滚动状态
                scrollingTimer = setTimeout(() => {
                  setIsScrolling(false);
                }, 300); // 给滚动动画足够的时间完成
              } else {
                // 如果没有容器，直接更新指示器
                updateIndicator(index);
              }
            } else {
              // 如果不需要滚动，直接更新指示器
              updateIndicator(index);
            }

            clearTimeout(timer);
          }, 100);
        }
      },
    ),
  );

  /** 组件挂载后初始化指示器位置和全局鼠标事件 */
  onMount(() => {
    const index = currentIndex();
    if (index >= 0) {
      // 初始化时直接设置位置 无动画
      updateIndicator(index, false);
    }

    // 只有在启用拖动功能时才添加全局鼠标事件监听
    if (DRAGGABLE) {
      // 使用节流优化全局鼠标移动事件
      let ticking = false;

      const handleGlobalMouseMove = (e: MouseEvent) => {
        if (!ticking) {
          requestAnimationFrame(() => {
            // 只有在鼠标按下且正在拖动时才处理移动
            if (isMouseDown() && isDragging()) {
              handleMouseMove(e);
            }
            ticking = false;
          });
          ticking = true;
        }
      };

      const handleGlobalMouseUp = () => {
        // 只有在鼠标按下时才处理松开事件
        if (isMouseDown()) {
          handleMouseUp();
        }
      };

      document.addEventListener("mousemove", handleGlobalMouseMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleGlobalMouseUp);

      // 清理事件监听
      onCleanup(() => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      });
    }
  });

  onCleanup(() => {
    if (hasMovedTimer) {
      clearTimeout(hasMovedTimer);
    }
    if (scrollingTimer) {
      clearTimeout(scrollingTimer);
    }
  });

  return (
    <div class={twMerge("w-[max-content] max-w-full", props.class)}>
      {/* 主容器 - 支持可选的鼠标拖动滚动 */}
      <div
        ref={setContainerRef}
        class={twMerge(
          "relative z-[1] flex shrink-0 select-none overflow-x-auto",
          // 根据方向设置布局
          isHorizontal()
            ? "w-full items-center"
            : "h-full flex-col items-start overflow-y-auto",
          // 根据是否启用拖动功能动态设置光标样式
          DRAGGABLE
            ? isDragging()
              ? "cursor-grabbing"
              : "cursor-grab"
            : "cursor-default",
          // 隐藏滚动条
          "scrollbar-none",
          props.containerClass,
        )}
        data-segment-container={currentIndex()}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {/* 动画指示器 - 跟随选中项移动 */}
        <div
          class={twMerge(
            "absolute z-[0]",
            enableAnimation() && "transition-transform duration-300 ease-out",
            props.indicatorClass,
          )}
          style={indicatorStyle()}
        />

        {/* 选项列表容器 - 确保内容不被压缩 */}
        <div
          class={twMerge(
            "relative z-[1] flex min-w-max",
            isHorizontal()
              ? "h-full items-center"
              : "w-full flex-col items-start",
            props.listClass,
          )}
          style={{
            gap: `${ITEM_SPACING}px`,
          }}
        >
          <For each={props.options}>
            {(option, index) => (
              <div
                onClick={() => handleOptionClick(option, index())}
                data-segment-item
                class={twMerge(
                  "delay-50 flex-shrink-0 cursor-pointer transition-all duration-200",
                  props.itemClass,
                  computeItemStateClass()(index()),
                )}
              >
                <div
                  class={twMerge(
                    "flex items-center",
                    isHorizontal() ? "gap-x-[4px]" : "gap-x-[10px]",
                    props.itemContentClass,
                  )}
                >
                  {/* 图标 - 仅在提供时显示 */}
                  <Show
                    when={option.icon && ICON_POSITION === "left"}
                    fallback={<></>}
                  >
                    <Icon
                      name={option.icon as svgPathTypes}
                      class={twMerge(
                        `size-[${ICON_SIZE}]`,
                        currentIndex() === index()
                          ? props.activeIconClass
                          : props.inactiveIconClass,
                      )}
                    />
                  </Show>

                  {/* 选项标签 */}
                  <Show
                    when={typeof option.label === "string"}
                    fallback={option.label}
                  >
                    <h2>{option.label as string}</h2>
                  </Show>
                  <Show
                    when={option.icon && ICON_POSITION === "right"}
                    fallback={<></>}
                  >
                    <Icon
                      name={option.icon as svgPathTypes}
                      class={twMerge(
                        `size-[${ICON_SIZE}]`,
                        currentIndex() === index()
                          ? props.activeIconClass
                          : props.inactiveIconClass,
                      )}
                    />
                  </Show>

                  {/* 数量徽章 - 仅在count大于0时显示 */}
                  <Show
                    when={option.count && option.count > 0}
                    fallback={<></>}
                  >
                    <div
                      class={twMerge(
                        "bg-general-main text-general-butzi flex items-center justify-center rounded-[5px] text-[13px] font-[700] leading-[18px]",
                        isHorizontal()
                          ? "ml-[4px] h-[18px] w-[18px]"
                          : "mt-[4px] h-[18px] w-[18px]",
                        option.type === 1
                          ? "bg-up-tips text-general-butzi"
                          : option.type === 2
                            ? "bg-general-highbut text-general-butzi"
                            : "",
                      )}
                    >
                      {option.count}
                    </div>
                  </Show>
                  {/* 红点提示 */}
                  <Show when={option.showRedDot} fallback={<></>}>
                    <AffiliateClaiTip class={props.redDotClass} />
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  );
}
